import useGetPaywallTemplates from "app/core/data/paywall/useGetPaywallTemplates"
import { trpc } from "app/core/trpcClient"
import { HoverSkew } from "app/splash/components/HoverSkew"
import moment from "moment"
import { queryTypes, useQueryState } from "next-usequerystate"
import { useRouter } from "next/router"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { AppRouter } from "app/api-utils/trpc/routers/_app"
import type { inferRouterOutputs } from "@trpc/server"
import { Skeleton } from "@superwall/ui/components/skeleton"
import { cn } from "@superwall/ui/lib/utils"
import { Input } from "@superwall/ui/components/input"
import { Button } from "@superwall/ui/components/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@superwall/ui/components/select"
import { ScrollFade } from "@superwall/ui/components/scroll-fade"
import TemplateModal from "./TemplateModal"

type RouterOutput = inferRouterOutputs<AppRouter>
type TPaywallTemplates = RouterOutput["blitzMigration"]["paywalls"]["getPaywallTemplates"]
type TTemplate = TPaywallTemplates["paywallTemplates"][number]
export type TTemplateSortBy = "createdAtAsc" | "createdAtDesc" | "updatedAtDesc"

interface TemplateGalleryProps {
  isPublic?: boolean
}

interface TemplateCardProps {
  template: TTemplate
  templateSortBy: TTemplateSortBy
  templatePreview?: any
  isPublic: boolean
  shouldOpen: boolean
  isUnlisted: boolean
}

const TemplateName = ({ name }: { name?: string }) => {
  const nameRef = useRef<HTMLDivElement>(null)

  const { offsetWidth, scrollWidth } = nameRef?.current || {
    offsetWidth: undefined,
    scrollWidth: undefined,
  }

  const showTooltip = useMemo(() => {
    return offsetWidth !== undefined && scrollWidth !== undefined
      ? offsetWidth < scrollWidth
      : false
  }, [offsetWidth, scrollWidth])

  return (
    <div className="relative group mb-0 w-full">
      <div
        ref={nameRef}
        className="w-full min-w-0 overflow-hidden text-accent-light text-center text-ellipsis whitespace-nowrap text-md"
      >
        {name}
      </div>
      <div
        className={cn(
          "absolute top-[26px] left-1/2 -translate-x-1/2 z-10 opacity-0 invisible shadow-lg rounded-lg py-1 px-3 bg-grayscale-medium text-sm text-white text-opacity-60 transition ease-in-out delay-150 duration-300",
          showTooltip && "group-hover:opacity-100 group-hover:visible"
        )}
      >
        {name}
      </div>
    </div>
  )
}

const PreviewImage = ({ url, placeholder }: { url?: string; placeholder?: string }) => {
  const [isError, setIsError] = useState(false)

  const checkImage = useCallback(() => {
    if (!url) return
    const image = new Image()
    image.src = url || ""

    image.onload = () => setIsError(false)
    image.onerror = () => setIsError(true)
  }, [url])

  useEffect(() => {
    checkImage()
  }, [checkImage])

  if (isError) {
    return (
      <div className="border border-white border-opacity-10 p-1.5 rounded-2xl shadow-2xl bg-grayscale-lightest/10 h-full w-full">
        <div className="h-full w-full flex items-center justify-center bg-grayscale-darker rounded-[12px] text-grayscale-lightest">
          Error loading image
        </div>
      </div>
    )
  }

  return (
    <div className="border border-white border-opacity-10 p-1.5 rounded-2xl shadow-2xl bg-grayscale-lightest/10 backdrop-blur-xl h-full w-full">
      {placeholder ? (
        <div className="h-full aspect-[9/19] w-full flex items-center justify-center bg-grayscale-darker rounded-[12px] text-grayscale-lightest">
          {placeholder}
        </div>
      ) : (
        <img className="rounded-[12px]" src={url} alt="Preview" />
      )}
    </div>
  )
}

const TemplateSkeleton = () => {
  return (
    <div className="flex flex-col space-y-3 w-full max-w-[300px]">
      <div className="space-y-2">
        <Skeleton className="h-4 w-3/4 mx-auto" />
        <Skeleton className="h-3 w-1/2 mx-auto" />
      </div>
      <Skeleton className="h-[500px] lg:h-[550px] 2xl:h-[650px] w-full rounded-2xl" />
    </div>
  )
}

const TemplatePreviewContent = ({
  template,
  templateSortBy,
  templatePreview,
  isPublic = false,
}: {
  template: TTemplate
  templateSortBy: TTemplateSortBy
  templatePreview: any
  isPublic?: boolean
}) => {
  return (
    <div className="cursor-pointer h-full flex flex-col w-full items-center justify-center">
      <TemplateName name={template.name} />
      <div className="text-center">
        {templateSortBy === "createdAtDesc" || templateSortBy === "createdAtAsc" ? (
          <div className="pb-3 text-xs text-secondary-foreground tracking-loose">
            Created {moment(template.createdAt).format("MMM D, YYYY")}
          </div>
        ) : (
          <div className="pb-3 text-xs text-secondary-foreground tracking-loose">
            Updated {moment(template.updatedAt).fromNow()}
          </div>
        )}
      </div>
      {isPublic ? (
        <HoverSkew
          key={template.id}
          className="!rounded-2xl h-full w-full"
          clickable
          glowStength="extra-light"
        >
          <div className="relative w-full h-full">
            <PreviewImage
              url={templatePreview?.url}
              placeholder={
                !templatePreview?.url
                  ? "Preview Pending"
                  : !templatePreview?.active
                    ? "Preview Updating ..."
                    : undefined
              }
            />
          </div>
        </HoverSkew>
      ) : (
        <div className="hover:scale-102 transition-transform duration-200 ease-out w-full h-full">
          <div className="relative w-full h-full">
            <PreviewImage
              url={templatePreview?.url}
              placeholder={
                !templatePreview?.url
                  ? "Preview Pending"
                  : !templatePreview?.active
                    ? "Preview Updating ..."
                    : undefined
              }
            />
          </div>
        </div>
      )}
    </div>
  )
}

const TemplateGalleryV2 = ({ isPublic = false }: TemplateGalleryProps) => {
  const router = useRouter()
  const [initialTemplateId] = useQueryState("templateId", queryTypes.integer)
  const [selectedCategory, setSelectedCategory] = useQueryState("category", queryTypes.string)
  const [searchQuery, setSearchQuery] = useQueryState("search", queryTypes.string.withDefault(""))
  const [templateSortBy, setTemplateSortBy] = useQueryState(
    "sort",
    queryTypes
      .stringEnum(["createdAtDesc", "createdAtAsc", "updatedAtDesc"] as const)
      .withDefault("createdAtDesc")
  )

  const [localSearchValue, setLocalSearchValue] = useState(searchQuery || "")

  useEffect(() => {
    setLocalSearchValue(searchQuery || "")
  }, [searchQuery])

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(localSearchValue || null)
    }, 300)

    return () => clearTimeout(timer)
  }, [localSearchValue, setSearchQuery])

  const { data, isLoading: isLoadingTemplates } = useGetPaywallTemplates(true, isPublic)

  const { data: unlistedTemplate } = trpc.paywalls.getUnlistedTemplateById.useQuery(
    { templateId: router.query.templateId as string },
    { enabled: isNaN(Number(initialTemplateId)) && !!router.query.templateId }
  )

  const { paywallTemplates: templates } = data || {}

  const sortedTemplates = useMemo(() => {
    const sortedRegularTemplates = (templates || []).sort((a, b) => {
      if (templateSortBy === "createdAtDesc") {
        return b.createdAt.getTime() - a.createdAt.getTime()
      }
      if (templateSortBy === "createdAtAsc") {
        return a.createdAt.getTime() - b.createdAt.getTime()
      }
      if (templateSortBy === "updatedAtDesc") {
        return b.updatedAt.getTime() - a.updatedAt.getTime()
      }

      return b.updatedAt.getTime() - a.updatedAt.getTime()
    })

    return unlistedTemplate ? [...sortedRegularTemplates, unlistedTemplate] : sortedRegularTemplates
  }, [templates, templateSortBy, unlistedTemplate])

  const allCategories = useMemo(() => {
    const categorySet = new Set<string>()

    sortedTemplates.forEach((template) => {
      let categories: string[] = []

      if (template.templateCategories) {
        try {
          categories =
            typeof template.templateCategories === "string"
              ? JSON.parse(template.templateCategories)
              : template.templateCategories
        } catch (e) {
          console.error("Error parsing templateCategories:", e)
        }
      }

      if (categories && categories.length > 0) {
        categories.forEach((cat) => categorySet.add(cat))
      }
    })

    return Array.from(categorySet).sort((a, b) => a.localeCompare(b))
  }, [sortedTemplates])

  const filteredTemplates = useMemo(() => {
    let filtered = sortedTemplates

    if (selectedCategory) {
      filtered = filtered.filter((template) => {
        let categories: string[] = []

        if (template.templateCategories) {
          try {
            categories =
              typeof template.templateCategories === "string"
                ? JSON.parse(template.templateCategories)
                : template.templateCategories
          } catch (e) {
            console.error("Error parsing templateCategories:", e)
          }
        }

        return categories && categories.length > 0 && categories.includes(selectedCategory)
      })
    }

    if (searchQuery?.trim()) {
      const query = searchQuery.toLowerCase().trim()
      filtered = filtered.filter((template) => template.name?.toLowerCase().includes(query))
    }

    return filtered
  }, [sortedTemplates, selectedCategory, searchQuery])

  const templatePreview = (template: TTemplate) => {
    if (!template) return
    const { id } = template
    const allPreviews = templates?.flatMap((template) => template.previews) || []
    const preview = allPreviews.find((preview) => preview?.paywallId === id && preview?.active)
    return preview
  }

  if (isLoadingTemplates) {
    return (
      <div className="flex flex-col items-center justify-center py-5 w-full">
        <div className="max-w-7xl w-full grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-8 lg:gap-x-5 md:gap-y-10 lg:gap-y-12">
          <TemplateSkeleton />
          <TemplateSkeleton />
          <TemplateSkeleton />
          <TemplateSkeleton />
        </div>
      </div>
    )
  }

  if (sortedTemplates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-5">
        <div className="flex justify-center items-center">
          <div className="my-8 text-lg text-muted-foreground">No templates found</div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center py-5">
      <div className="w-full max-w-7xl space-y-6">
        <div className=" flex flex-col-reverse sm:flex-row gap-2 sm:gap-3.5 justify-between sm:items-center">
          <div className="relative w-full sm:w-fit overflow-hidden">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0">
              <ScrollFade
                showLeft={true}
                showRight={true}
                showTop={false}
                showBottom={false}
                fadeWidth={24}
              />
              <Button
                variant="outline"
                size="sm"
                isLoading={false}
                onClick={() => setSelectedCategory(null)}
                className={cn(
                  "rounded-lg whitespace-nowrap",
                  selectedCategory === null &&
                    "bg-[rgba(116,255,241,0.1)] text-brand-primary shadow-xs hover:bg-[rgba(116,255,241,0.15)] hover:text-brand-primary/80 border-brand-primary/20"
                )}
              >
                All
              </Button>
              {allCategories.map((category) => (
                <Button
                  key={category}
                  variant="outline"
                  size="sm"
                  isLoading={false}
                  onClick={() => setSelectedCategory(category)}
                  className={cn(
                    "rounded-lg whitespace-nowrap",
                    selectedCategory === category &&
                      "bg-[rgba(116,255,241,0.1)] text-brand-primary shadow-xs hover:bg-[rgba(116,255,241,0.15)] hover:text-brand-primary/80 border-brand-primary/20"
                  )}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
          <div className="flex flex-row gap-2 shrink-0 items-start sm:items-center">
            <Select
              value={templateSortBy}
              onValueChange={(value) => setTemplateSortBy(value as TTemplateSortBy)}
            >
              <SelectTrigger className="shrink-0 w-fit rounded-lg">
                <SelectValue placeholder="Sort by..." className="flex items-center gap-2" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="createdAtDesc">
                  <ClockUpIcon /> Newest First
                </SelectItem>
                <SelectItem value="createdAtAsc">
                  <ClockDownIcon /> Oldest First
                </SelectItem>
                <SelectItem value="updatedAtDesc">
                  <BellIcon /> Recently Updated
                </SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="text"
              placeholder="Search templates..."
              className="min-w-[175px] rounded-lg"
              value={localSearchValue}
              onChange={(e) => setLocalSearchValue(e.target.value)}
            />
          </div>
        </div>

        {filteredTemplates.length === 0 ? (
          <div className="flex justify-center items-center py-12">
            <div className="text-muted-foreground">
              No templates found {selectedCategory && `in ${selectedCategory}`}
              {searchQuery && ` matching "${searchQuery}"`}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-8 lg:gap-x-5 md:gap-y-10 lg:gap-y-12">
            {filteredTemplates.map((template) => {
              const shouldOpen = initialTemplateId === template.id
              const isUnlisted = template.id === unlistedTemplate?.id
              return (
                <div className="flex-grow" key={`template-${template.name}-${template.id}`}>
                  <TemplateCard
                    template={template}
                    templateSortBy={templateSortBy}
                    templatePreview={templatePreview(template)}
                    isPublic={isPublic}
                    shouldOpen={shouldOpen}
                    isUnlisted={isUnlisted}
                  />
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

const TemplateCard = ({
  template,
  templateSortBy,
  templatePreview,
  isPublic,
  shouldOpen,
  isUnlisted,
}: TemplateCardProps) => {
  return (
    <div className="flex flex-col justify-start items-center transition-all ease-out h-full hover:border-border-lighter duration-[0.2s]">
      <TemplateModal
        template={template}
        initiallyOpen={isUnlisted ? true : shouldOpen}
        isUnlisted={isUnlisted}
        isPublic={isPublic}
        syncWithUrl={isPublic}
      >
        <div className="w-full">
          <TemplatePreviewContent
            template={template}
            templateSortBy={templateSortBy}
            templatePreview={templatePreview}
            isPublic={isPublic}
          />
        </div>
      </TemplateModal>
    </div>
  )
}

const ClockUpIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M13.228 21.925A10 10 0 1 1 21.994 12.338" />
    <path d="M12 6v6l1.562.781" />
    <path d="m14 18 4-4 4 4" />
    <path d="M18 22v-8" />
  </svg>
)

const ClockDownIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12.338 21.994A10 10 0 1 1 21.925 13.227" />
    <path d="M12 6v6l2 1" />
    <path d="m14 18 4 4 4-4" />
    <path d="M18 14v8" />
  </svg>
)

const BellIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M19.3 14.8C20.1 16.4 21 17 21 17H3s3-2 3-9c0-3.3 2.7-6 6-6 1 0 1.9.2 2.8.7" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    <path d="M15 8h6" />
    <path d="M18 5v6" />
  </svg>
)

export default TemplateGalleryV2
